"use strict";
/**
 * Validation utility functions
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PASSWORD_MAX_LENGTH = exports.PASSWORD_MIN_LENGTH = exports.MAX_FILE_NAME_LENGTH = exports.EMAIL_REGEX = void 0;
exports.isValidEmail = isValidEmail;
exports.normalizeEmail = normalizeEmail;
exports.normalizeRecipients = normalizeRecipients;
exports.isValidFileSize = isValidFileSize;
exports.sanitizeFileName = sanitizeFileName;
exports.isValidMimeType = isValidMimeType;
exports.isValidExpiryDate = isValidExpiryDate;
exports.isValidFileStatus = isValidFileStatus;
exports.isValidShareStatus = isValidShareStatus;
exports.isValidAuthMethod = isValidAuthMethod;
exports.isValidOIDCProvider = isValidOIDCProvider;
exports.validateRecipients = validateRecipients;
exports.validatePasswordPolicy = validatePasswordPolicy;
exports.validateFileIds = validateFileIds;
/**
 * Email validation regex (RFC 5322 simplified, strict)
 * CR-041: 連続ドット・先頭/末尾ドットを排除し、ドメインはラベル+TLD(2文字以上)を必須とする。
 * - local: ドット区切りの atom（atom 内は英数字と `_ % + -`）。先頭/末尾/連続ドット不可。
 * - domain: 各ラベルはハイフン先頭/末尾不可、最後に 2 文字以上の英字 TLD。
 * プロジェクト全体の唯一の正規表現定義（全箇所からこの定義/`isValidEmail` を参照すること）。
 */
exports.EMAIL_REGEX = /^[a-zA-Z0-9_%+-]+(?:\.[a-zA-Z0-9_%+-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
/**
 * Validate email address format
 */
function isValidEmail(email) {
    return exports.EMAIL_REGEX.test(email);
}
/**
 * メールアドレスを正規化する (CR-041)。
 * 前後の空白を除去し、小文字に統一する（メールアドレスは大文字小文字非区別として扱う）。
 */
function normalizeEmail(email) {
    return email.trim().toLowerCase();
}
/**
 * 宛先メールアドレスを正規化し、重複を除去する (CR-041)。
 * - 各要素を `normalizeEmail` で正規化
 * - 空文字を除外
 * - 重複を除去（入力順を保持）
 */
function normalizeRecipients(recipients) {
    const seen = new Set();
    const result = [];
    for (const raw of recipients) {
        const email = normalizeEmail(raw);
        if (email.length === 0 || seen.has(email)) {
            continue;
        }
        seen.add(email);
        result.push(email);
    }
    return result;
}
/**
 * Validate file size against maximum limit
 */
function isValidFileSize(fileSize, maxSize) {
    return fileSize >= 0 && fileSize <= maxSize;
}
/**
 * ファイル名の最大長（バイトではなく文字数）
 */
exports.MAX_FILE_NAME_LENGTH = 255;
// 制御文字（NUL〜US および DEL）
const FILE_NAME_CONTROL_CHARS = /[\x00-\x1F\x7F]/g;
/**
 * ユーザー指定のファイル名を安全な basename に正規化する (CR-010)。
 * - パス区切り（`/` `\`）以降の basename のみ採用し、`../` 等のパストラバーサルを除去
 * - 制御文字を除去
 * - 純粋なドット名（`.` / `..`）は空文字に
 *
 * @returns 正規化後の basename（有効な文字が残らない場合は空文字）
 */
function sanitizeFileName(fileName) {
    // 最後のパス区切り以降を取り出す（`foo/bar`・`foo\bar`・`../etc` を basename 化）
    const base = fileName.split(/[/\\]/).pop() ?? '';
    const cleaned = base.replace(FILE_NAME_CONTROL_CHARS, '').trim();
    if (cleaned === '.' || cleaned === '..') {
        return '';
    }
    return cleaned;
}
// MIMEタイプの形式（type/subtype。RFCで許可されるトークン文字に限定）
const MIME_TYPE_REGEX = /^[a-zA-Z0-9!#$&^_.+-]+\/[a-zA-Z0-9!#$&^_.+-]+$/;
/**
 * MIMEタイプの形式・最大長を検証する (CR-010)。
 * 許可リスト検証ではなく、不正文字（制御文字・ヘッダ注入等）と長さの検証。
 */
function isValidMimeType(mimeType) {
    return mimeType.length <= 255 && MIME_TYPE_REGEX.test(mimeType);
}
/**
 * Validate expiry date is in the future and within max days
 */
function isValidExpiryDate(expiryDate, maxDays) {
    try {
        const expiry = new Date(expiryDate);
        const now = new Date();
        // Check if date is valid
        if (isNaN(expiry.getTime())) {
            return false;
        }
        // Check if date is in the future
        if (expiry <= now) {
            return false;
        }
        // Check max days if specified
        // CR-047: setDate/getDate はローカル（Lambda=UTC）のカレンダー日加算となり、
        // create-share.ts のミリ秒基準（now + maxDays*24h）と最大で時差ぶんずれる。
        // 基準をミリ秒（絶対時刻）に統一し、タイムゾーン非依存にする。
        if (maxDays !== undefined) {
            const maxTimestamp = now.getTime() + maxDays * 24 * 60 * 60 * 1000;
            if (expiry.getTime() > maxTimestamp) {
                return false;
            }
        }
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Validate file status value
 */
function isValidFileStatus(status) {
    // FileStatus の全メンバと同期（CR-042: scanning / infected 欠落を修正）
    return ['pending', 'scanning', 'active', 'infected', 'expired', 'deleted'].includes(status);
}
/**
 * Validate share status value
 */
function isValidShareStatus(status) {
    return ['active', 'expired', 'revoked'].includes(status);
}
/**
 * Validate authentication method
 */
function isValidAuthMethod(method) {
    return ['email', 'oidc'].includes(method);
}
/**
 * Validate OIDC provider
 */
function isValidOIDCProvider(provider) {
    return ['google', 'microsoft', 'dfgate'].includes(provider);
}
/**
 * Validate recipient email addresses
 */
function validateRecipients(recipients, maxRecipients = 100) {
    const errors = [];
    // Check if empty
    if (recipients.length === 0) {
        errors.push('At least one recipient is required');
    }
    // Check max recipients
    if (recipients.length > maxRecipients) {
        errors.push(`Too many recipients (max: ${maxRecipients})`);
    }
    // Validate each email
    recipients.forEach((email) => {
        if (!isValidEmail(email)) {
            errors.push(`Invalid email address: ${email}`);
        }
    });
    // CR-041: 正規化後の重複宛先を検出（大文字小文字・前後空白の違いも重複として扱う）
    const seen = new Set();
    recipients.forEach((email) => {
        const normalized = normalizeEmail(email);
        if (seen.has(normalized)) {
            errors.push(`Duplicate recipient: ${email}`);
        }
        else {
            seen.add(normalized);
        }
    });
    return {
        valid: errors.length === 0,
        errors,
    };
}
/**
 * パスワードポリシーの長さ範囲（CR-051: プロジェクト全体で一元化）。
 * - 最小: 8文字
 * - 最大: 128文字（極端に長い入力によるDoS/不整合を防止）
 */
exports.PASSWORD_MIN_LENGTH = 8;
exports.PASSWORD_MAX_LENGTH = 128;
/**
 * Validate password against security policy.
 * Policy: 8-128 chars, uppercase, lowercase, digit, symbol required.
 * Optionally checks if password is same as current.
 * CR-051: 記号判定は「英数字以外の任意の文字」(`/[^A-Za-z0-9]/`) とし、
 * バッククォート/チルダ/Unicode 記号などを含む強いパスフレーズを誤拒否しない。
 */
function validatePasswordPolicy(password, currentPassword) {
    const violations = [];
    if (password.length < exports.PASSWORD_MIN_LENGTH) {
        violations.push('minLength');
    }
    if (password.length > exports.PASSWORD_MAX_LENGTH) {
        violations.push('maxLength');
    }
    if (!/[A-Z]/.test(password)) {
        violations.push('uppercase');
    }
    if (!/[a-z]/.test(password)) {
        violations.push('lowercase');
    }
    if (!/[0-9]/.test(password)) {
        violations.push('digit');
    }
    // 英数字以外（記号・空白・Unicode等）を1文字以上要求
    if (!/[^A-Za-z0-9]/.test(password)) {
        violations.push('symbol');
    }
    if (currentPassword !== undefined && password === currentPassword) {
        violations.push('sameAsCurrent');
    }
    return {
        valid: violations.length === 0,
        violations,
    };
}
/**
 * Validate file IDs array
 */
function validateFileIds(fileIds, maxFiles = 10) {
    const errors = [];
    // Check if empty
    if (fileIds.length === 0) {
        errors.push('At least one file ID is required');
    }
    // Check max files
    if (fileIds.length > maxFiles) {
        errors.push(`Too many files (max: ${maxFiles})`);
    }
    // Check for empty IDs
    fileIds.forEach((id, index) => {
        if (!id || id.trim() === '') {
            errors.push(`File ID at index ${index} is empty`);
        }
    });
    return {
        valid: errors.length === 0,
        errors,
    };
}
//# sourceMappingURL=validation.js.map